import { loanApi } from "@api/loans";
import { fineApi } from "@api/fine/fine";

export class LoanStatusSyncManager {
  constructor() {
    this.LOAN_STATES = {
      ITEM_ON_LOAN: "ITEM_ON_LOAN",
      ITEM_RETURNED: "ITEM_RETURNED",
      ITEM_OVERDUE: "ITEM_OVERDUE",
      ITEM_LOST: "ITEM_LOST",
      CANCELLED: "CANCELLED",
    };

    this.FINE_STATES = {
      ACTIVE: "active",
      PAID: "paid",
      WAIVED: "waived",
      CANCELLED: "cancelled",
    };

    this.MAX_VERIFICATION_ATTEMPTS = 3;
    this.VERIFICATION_DELAYS = [500, 1000, 2000];
    this.RETURNABLE_STATES = ["ITEM_ON_LOAN", "ITEM_OVERDUE"];
    this.RETRY_MESSAGES = [
      "updated_by",
      "created_by",
      "user_id",
      "validation error",
      "field not allowed",
      "invalid field",
    ];
  }

  async processAtomicFinePaymentAndReturn(
    fineId,
    paymentData,
    progressCallback = null
  ) {
    const result = this.createEmptyResult(fineId);

    try {
      this.updateProgress(
        progressCallback,
        "fetching_fine",
        `Getting details for fine ${fineId}...`
      );

      const fineDetails = await this.getFineDetails(fineId);
      if (!fineDetails) {
        throw new Error(`Cannot retrieve fine details for ${fineId}`);
      }

      const loanId = fineDetails.loan_id || fineDetails.loanId;
      if (!loanId) {
        throw new Error(`No loan ID found for fine ${fineId}`);
      }

      result.return.loanId = loanId;

      this.updateProgress(
        progressCallback,
        "payment",
        `Paying fine ${fineId}...`
      );
      const paymentResult = await this.payFineImmediately(fineId, paymentData);

      if (!paymentResult.success) {
        result.payment.error = paymentResult.error;
        throw new Error(`Payment failed: ${paymentResult.error}`);
      }

      result.payment.success = true;
      result.payment.data = paymentResult.data;

      this.updateProgress(
        progressCallback,
        "returning",
        `Immediately returning loan ${loanId}...`
      );
      const returnResult = await this.returnLoanImmediately(loanId, {
        ...paymentData,
        fineId,
        paymentReference: paymentResult.paymentReference,
        reason: "immediate_return_after_payment",
      });

      if (!returnResult.success) {
        result.return.error = returnResult.error;
      } else {
        result.return.success = true;
        result.return.data = returnResult.data;

        this.updateProgress(
          progressCallback,
          "verifying",
          `Verifying return for loan ${loanId}...`
        );
        result.verification = await this.quickVerifyReturn(loanId);
      }

      this.updateProgress(
        progressCallback,
        "complete",
        `Fine ${fineId} paid and loan ${loanId} returned successfully`
      );

      return result;
    } catch (error) {
      result.payment.error = result.payment.error || error.message;
      this.updateProgress(
        progressCallback,
        "error",
        `Process failed for fine ${fineId}: ${error.message}`,
        true
      );
      return result;
    }
  }

  async processMultipleAtomicOperations(
    fineIds,
    paymentData,
    progressCallback = null
  ) {
    const results = {
      processed: [],
      summary: {
        total: fineIds.length,
        paidFines: 0,
        returnedLoans: 0,
        verifiedReturns: 0,
        errors: 0,
      },
    };

    for (let i = 0; i < fineIds.length; i++) {
      const fineId = fineIds[i];

      try {
        this.updateProgress(
          progressCallback,
          "processing",
          `Processing fine ${i + 1}/${fineIds.length} (${fineId})...`,
          false,
          {
            current: i + 1,
            total: fineIds.length,
            percentage: Math.round(((i + 1) / fineIds.length) * 100),
          }
        );

        const atomicResult = await this.processAtomicFinePaymentAndReturn(
          fineId,
          {
            ...paymentData,
            payment_reference: this.generatePaymentReference(
              paymentData.payment_reference,
              fineId
            ),
          },
          progressCallback
        );

        results.processed.push(atomicResult);
        this.updateSummary(results.summary, atomicResult);
      } catch (error) {
        const errorResult = this.createErrorResult(fineId, error.message);
        results.processed.push(errorResult);
        results.summary.errors++;
      }
    }

    this.updateProgress(
      progressCallback,
      "complete",
      `Completed! ${results.summary.paidFines} fines paid, ${results.summary.returnedLoans} loans returned`,
      false,
      { current: fineIds.length, total: fineIds.length, percentage: 100 }
    );

    return results;
  }

  async payFineImmediately(fineId, paymentData) {
    const cleanPaymentData = {
      payment_reference:
        paymentData.payment_reference || `ATOMIC_${Date.now()}_${fineId}`,
      notes: paymentData.notes || `Atomic payment for fine ${fineId}`,
    };

    try {
      const response = await fineApi.pay(fineId, cleanPaymentData);

      if (response.status === 200 || response.status === 201) {
        return this.createSuccessResponse(
          fineId,
          response.data,
          cleanPaymentData.payment_reference
        );
      } else {
        throw new Error(
          `Unexpected payment response status: ${response.status}`
        );
      }
    } catch (error) {
      if (this.shouldRetryPayment(error)) {
        try {
          const minimalPaymentData = {
            payment_reference: `RETRY_${Date.now()}_${fineId}`,
          };
          const retryResponse = await fineApi.pay(fineId, minimalPaymentData);

          if (retryResponse.status === 200 || retryResponse.status === 201) {
            return this.createSuccessResponse(
              fineId,
              retryResponse.data,
              minimalPaymentData.payment_reference,
              true
            );
          }
        } catch (retryError) {
          // Retry failed, fall through to return original error
        }
      }

      return this.createErrorResponse(fineId, error);
    }
  }

  async returnLoanImmediately(loanId, options = {}) {
    try {
      const loanResponse = await loanApi.get(loanId);
      const loan = loanResponse.data;
      const currentStatus = loan.metadata?.state || loan.state || loan.status;

      if (!this.RETURNABLE_STATES.includes(currentStatus)) {
        return {
          success: false,
          loanId,
          error: `Loan is in ${currentStatus} state and cannot be returned`,
          currentStatus,
          timestamp: new Date().toISOString(),
        };
      }

      const returnData = {
        transaction_date: new Date().toISOString(),
        notes:
          options.notes ||
          `Immediate return after fine ${options.fineId} payment`,
        source: "atomic_payment_return",
        payment_reference: options.paymentReference,
        force_return: true,
      };

      const returnMethods = [
        () => this.tryCheckinAction(loanId, loan, returnData),
        () => this.tryCheckoutReturn(loanId, returnData),
        () => this.tryDirectAPICall(loan, returnData),
      ];

      for (const method of returnMethods) {
        const result = await method();
        if (result.success) {
          return {
            success: true,
            loanId,
            previousStatus: currentStatus,
            newStatus: "ITEM_RETURNED",
            data: result.data,
            timestamp: new Date().toISOString(),
          };
        }
      }

      throw new Error("All return methods failed");
    } catch (error) {
      return {
        success: false,
        loanId,
        error: error.message || "Return operation failed",
        details: error.response?.data,
        timestamp: new Date().toISOString(),
      };
    }
  }

  async quickVerifyReturn(loanId) {
    for (
      let attempt = 1;
      attempt <= this.MAX_VERIFICATION_ATTEMPTS;
      attempt++
    ) {
      try {
        if (attempt > 1) {
          const delay = this.VERIFICATION_DELAYS[attempt - 2] || 2000;
          await new Promise((resolve) => setTimeout(resolve, delay));
        }

        const response = await loanApi.get(loanId, {
          params: {
            _t: Date.now(),
            _refresh: "true",
            force_refresh: attempt > 1,
          },
        });

        const loan = response.data;
        const currentStatus = loan.metadata?.state || loan.state || loan.status;

        if (currentStatus === "ITEM_RETURNED") {
          return {
            success: true,
            verifiedStatus: currentStatus,
            attempts: attempt,
          };
        }
      } catch (error) {
        // Continue to next attempt
      }
    }

    return {
      success: false,
      attempts: this.MAX_VERIFICATION_ATTEMPTS,
    };
  }

  async completePaymentAndReturnWorkflow(
    fineIds,
    paymentData,
    progressCallback = null
  ) {
    const results = await this.processMultipleAtomicOperations(
      fineIds,
      paymentData,
      progressCallback
    );

    return {
      payment: {
        successful: this.filterAndMapResults(
          results.processed,
          (r) => r.payment.success,
          (r) => ({
            fineId: r.fineId,
            loanId: r.return.loanId,
            result: r.payment.data,
            timestamp: r.timestamp,
          })
        ),
        failed: this.filterAndMapResults(
          results.processed,
          (r) => !r.payment.success,
          (r) => ({
            fineId: r.fineId,
            error: r.payment.error,
            timestamp: r.timestamp,
          })
        ),
      },
      returns: {
        successful: this.filterAndMapResults(
          results.processed,
          (r) => r.return.success,
          (r) => ({
            loanId: r.return.loanId,
            previousStatus: r.return.data?.previousStatus || "ITEM_ON_LOAN",
            newStatus: "ITEM_RETURNED",
            success: true,
            timestamp: r.timestamp,
          })
        ),
        failed: this.filterAndMapResults(
          results.processed,
          (r) => r.payment.success && !r.return.success,
          (r) => ({
            loanId: r.return.loanId,
            error: r.return.error,
            timestamp: r.timestamp,
          })
        ),
        skipped: [],
      },
      verification: {
        successful: this.filterAndMapResults(
          results.processed,
          (r) => r.verification.success,
          (r) => ({
            fineId: r.fineId,
            loanId: r.return.loanId,
            verifiedStatus: r.verification.verifiedStatus,
            timestamp: r.timestamp,
          })
        ),
        failed: this.filterAndMapResults(
          results.processed,
          (r) => !r.verification.success,
          (r) => ({
            fineId: r.fineId,
            loanId: r.return.loanId,
            reason: "Verification failed",
            timestamp: r.timestamp,
          })
        ),
      },
      summary: results.summary,
    };
  }

  // Helper methods
  async getFineDetails(fineId) {
    try {
      const response = await fineApi.get(fineId);
      return response.data;
    } catch (error) {
      return null;
    }
  }

  shouldRetryPayment(error) {
    const errorMessage = error.message || error.response?.data?.message || "";
    return this.RETRY_MESSAGES.some((msg) =>
      errorMessage.toLowerCase().includes(msg.toLowerCase())
    );
  }

  async tryCheckinAction(loanId, loan, returnData) {
    try {
      const availableActions = loan.availableActions || {};
      if (!availableActions.checkin) return { success: false };

      const response = await loanApi.doAction(
        `/circulation/loans/${loanId}/checkin`,
        loan.metadata?.document_pid || loan.document_pid,
        loan.metadata?.patron_pid || loan.patron_pid,
        {
          ...returnData,
          itemPid: loan.metadata?.item_pid || loan.item_pid,
        }
      );

      return response.status === 200 || response.status === 202
        ? { success: true, data: response.data }
        : { success: false };
    } catch (error) {
      return { success: false };
    }
  }

  async tryCheckoutReturn(loanId, returnData) {
    try {
      const response = await loanApi.doAction(
        loanId,
        "checkout-return",
        returnData
      );
      return response.status === 200 || response.status === 202
        ? { success: true, data: response.data }
        : { success: false };
    } catch (error) {
      return { success: false };
    }
  }

  async tryDirectAPICall(loan, returnData) {
    try {
      const availableActions = loan.availableActions || {};
      if (!availableActions.checkin) return { success: false };

      const response = await fetch(availableActions.checkin, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(returnData),
      });

      if (response.ok) {
        const responseData = await response.json();
        return { success: true, data: responseData };
      }
      return { success: false };
    } catch (error) {
      return { success: false };
    }
  }

  createEmptyResult(fineId) {
    return {
      fineId,
      payment: { success: false, data: null, error: null },
      return: { success: false, data: null, error: null, loanId: null },
      verification: { success: false, attempts: 0 },
      timestamp: new Date().toISOString(),
    };
  }

  createErrorResult(fineId, error) {
    return {
      fineId,
      payment: { success: false, error },
      return: { success: false, error: "Not attempted due to payment failure" },
      verification: { success: false, attempts: 0 },
      timestamp: new Date().toISOString(),
    };
  }

  createSuccessResponse(fineId, data, paymentReference, wasRetry = false) {
    return {
      success: true,
      fineId,
      data,
      paymentReference,
      timestamp: new Date().toISOString(),
      ...(wasRetry && { wasRetry: true }),
    };
  }

  createErrorResponse(fineId, error) {
    return {
      success: false,
      fineId,
      error: error.message || "Payment failed",
      details: error.response?.data,
      timestamp: new Date().toISOString(),
    };
  }

  updateProgress(
    progressCallback,
    stage,
    message,
    isError = false,
    extra = {}
  ) {
    if (progressCallback) {
      progressCallback({
        stage,
        message,
        ...(isError && { error: true }),
        ...extra,
      });
    }
  }

  updateSummary(summary, atomicResult) {
    if (atomicResult.payment.success) summary.paidFines++;
    if (atomicResult.return.success) summary.returnedLoans++;
    if (atomicResult.verification.success) summary.verifiedReturns++;
    if (!atomicResult.payment.success || !atomicResult.return.success)
      summary.errors++;
  }

  generatePaymentReference(baseReference, fineId) {
    return `${baseReference || "AUTO"}_${fineId}_${Date.now()}`;
  }

  filterAndMapResults(processed, filterFn, mapFn) {
    return processed.filter(filterFn).map(mapFn);
  }
}

export default LoanStatusSyncManager;
